/**
 * Browser compatibility utilities to handle various browser issues
 */
import { safeStorage } from './trackingUtils';
import { logger } from "./logger";
// import { logger } from "./logger";
// Track if we've already logged compatibility warnings
let compatibilityWarningsLogged = new Set<string>();
/**
 * Check if we're running in a problematic browser environment
 */
export const checkBrowserCompatibility = (): {
    hasTrackingPrevention: boolean;
    hasExtensionConflicts: boolean;
    isPrivateMode: boolean;
    storageAvailable: boolean;
    recommendations: string[];
} => {
    const issues: string[] = [];
    const recommendations: string[] = [];
    // Check for tracking prevention
    let hasTrackingPrevention = false;
    try {
        const testKey = '__compatibility_test__';
        safeStorage.setItem(testKey, 'test');
        const testValue = safeStorage.getItem(testKey);
        safeStorage.removeItem(testKey);
        if (testValue !== 'test') {
            hasTrackingPrevention = true;
            issues.push('Browser storage access is blocked');
            recommendations.push('Consider enabling cookies and site data for this site');
        }
    }
    catch (error) {
        // Only consider it tracking prevention if it's a specific storage error
        if (error instanceof DOMException && error.code === DOMException.QUOTA_EXCEEDED_ERR) {
            hasTrackingPrevention = true;
            issues.push('Browser storage access failed');
            recommendations.push('Check browser privacy settings');
        }
        // For other errors, don't assume tracking prevention
    }
    // Check for private/incognito mode
    // NOTE: This detection method may produce false positives in some Safari versions
    // or Firefox configurations. It's a best-effort detection based on storage access.
    let isPrivateMode = false;
    try {
        const testKey = '__private_test__';
        safeStorage.setItem(testKey, 'test');
        safeStorage.removeItem(testKey);
        isPrivateMode = false;
    }
    catch (error) {
        // Only consider it private mode if it's a specific storage quota error
        if (error instanceof DOMException && error.code === DOMException.QUOTA_EXCEEDED_ERR) {
            isPrivateMode = true;
            issues.push('Running in private/incognito mode (detected via storage access)');
            recommendations.push('Some features may not work in private mode');
        }
        // For other errors, don't assume private mode
    }
    // Check for extension conflicts
    let hasExtensionConflicts = false;
    try {
        // Check for common problematic extensions via multiple methods
        const extensionIndicators = [
            // URL-based detection
            'chrome-extension://',
            'moz-extension://',
            'safari-extension://',
            'mfbcdcnpokpoajjciilocoachedjkima', // Common AI completion extension
        ];
        // Check URLs and referrer
        const urlBasedDetection = extensionIndicators.some(ext => window.location.href.includes(ext) ||
            document.referrer.includes(ext));
        // Check for extension runtime APIs (more reliable)
        const runtimeBasedDetection = !!((window as any).chrome?.runtime ||
            (window as any).browser?.runtime ||
            (window as any).safari?.extension);
        // Check for extension-injected globals
        const globalBasedDetection = !!((window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__ ||
            (window as any).__REDUX_DEVTOOLS_EXTENSION__ ||
            (window as any).__VUE_DEVTOOLS_GLOBAL_HOOK__);
        if (urlBasedDetection || runtimeBasedDetection || globalBasedDetection) {
            hasExtensionConflicts = true;
            issues.push('Browser extension conflicts detected');
            recommendations.push('Try disabling browser extensions temporarily');
        }
    }
    catch (error) {
        // Extension detection failed, assume no conflicts
    }
    // Check overall storage availability
    const storageAvailable = !hasTrackingPrevention && !isPrivateMode;
    // Log warnings only once per session (with conditional logging)
    if (issues.length > 0) {
        const warningKey = issues.sort().join('|');
        if (!compatibilityWarningsLogged.has(warningKey)) {
            const shouldLog = import.meta.env.MODE === 'development' || import.meta.env.MODE === 'test';
            if (shouldLog) {
                logger.info('[Browser Compatibility] Detected issues:', { "arg0": issues });
                logger.info('[Browser Compatibility] Recommendations:', { "arg0": recommendations });
            }
            compatibilityWarningsLogged.add(warningKey);
        }
    }
    return {
        hasTrackingPrevention,
        hasExtensionConflicts,
        isPrivateMode,
        storageAvailable,
        recommendations
    };
};
/**
 * Suppress Chrome extension errors in console
 */
export const suppressExtensionErrors = (): void => {
    // Override console.error to filter out extension-related errors
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
        const message = args.join(' ');
        // Filter out common extension errors
        const extensionErrorPatterns = [
            'runtime.lastError',
            'The message port closed before a response was received',
            'Cannot create item with duplicate id',
            'FrameDoesNotExistError',
            'The page keeping the extension port is moved into back/forward cache',
            'Could not establish connection',
            'Could not establish connection. Receiving end does not exist',
            'Receiving end does not exist',
            'chrome-extension://',
            'net::ERR_FILE_NOT_FOUND',
            'ERR_FILE_NOT_FOUND',
            'Attempting to use a disconnected port object',
            'Unchecked runtime.lastError',
            'Unchecked runtime.lastError:',
            'Error handling response: Error: Attempting to use a disconnected port object',
            'background.js',
            'content_script.js',
            'utils.js',
            'extensionState.js',
            'heuristicsRedefinitions.js',
            'DelayedMessageSender'
        ];
        const isExtensionError = extensionErrorPatterns.some(pattern => message.includes(pattern));
        if (!isExtensionError) {
            originalConsoleError.apply(console, args);
        }
    };
    logger.error('[Browser Compatibility] Extension error suppression active', undefined, {});
};
/**
 * Initialize browser compatibility checks
 */
export const initializeBrowserCompatibility = (): void => {
    // Check compatibility on page load
    const compatibility = checkBrowserCompatibility();
    // Suppress extension errors
    suppressExtensionErrors();
    // Store compatibility info for other components
    if (typeof window !== 'undefined') {
        (window as Window & {
            __browserCompatibility?: ReturnType<typeof checkBrowserCompatibility>;
        }).__browserCompatibility = compatibility;
        // Set global flag for storage issues (consolidated)
        (window as any).__apt_storage_blocked__ = !compatibility.storageAvailable;
    }
    // Log summary with conditional logging for production
    const shouldLog = import.meta.env.MODE === 'development' || import.meta.env.MODE === 'test';
    if (compatibility.storageAvailable) {
        if (shouldLog) {
            logger.info('[Browser Compatibility] All systems operational');
        }
    }
    else {
        if (shouldLog) {
            logger.info('[Browser Compatibility] Some features may be limited due to browser restrictions');
            // Only log storage warning if it's actually a problem, not just a fallback
            if (compatibility.hasTrackingPrevention || compatibility.isPrivateMode) {
                logger.info('[Browser Compatibility] Using fallback storage mechanisms');
            }
        }
    }
};
/**
 * Get current browser compatibility status
 */
export const getBrowserCompatibility = () => {
    if (typeof window !== 'undefined' && (window as Window & {
        __browserCompatibility?: ReturnType<typeof checkBrowserCompatibility>;
    }).__browserCompatibility) {
        return (window as Window & {
            __browserCompatibility?: ReturnType<typeof checkBrowserCompatibility>;
        }).__browserCompatibility!;
    }
    return checkBrowserCompatibility();
};
/**
 * Check if a specific feature is available
 *
 * Future-proof feature detection for various browser capabilities
 */
export const isFeatureAvailable = (feature: 'storage' | 'localStorage' | 'cookies' | 'analytics' | 'serviceWorker' | 'webRTC' | 'webGL' | 'webAudio' | 'geolocation' | 'notifications' | 'push' | 'indexedDB'): boolean => {
    const compatibility = getBrowserCompatibility();
    switch (feature) {
        case 'storage':
        case 'localStorage':
            return compatibility.storageAvailable;
        case 'cookies':
            return compatibility.storageAvailable && !compatibility.isPrivateMode;
        case 'analytics':
            return compatibility.storageAvailable && !compatibility.hasTrackingPrevention;
        case 'serviceWorker':
            return 'serviceWorker' in navigator && compatibility.storageAvailable;
        case 'webRTC':
            return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) &&
                !compatibility.hasTrackingPrevention;
        case 'webGL':
            try {
                const canvas = document.createElement('canvas');
                return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
            }
            catch {
                return false;
            }
        case 'webAudio':
            return !!(window.AudioContext || (window as any).webkitAudioContext);
        case 'geolocation':
            return 'geolocation' in navigator && !compatibility.hasTrackingPrevention;
        case 'notifications':
            return 'Notification' in window && !compatibility.hasTrackingPrevention;
        case 'push':
            return 'serviceWorker' in navigator && 'PushManager' in window &&
                compatibility.storageAvailable && !compatibility.hasTrackingPrevention;
        case 'indexedDB':
            return 'indexedDB' in window && compatibility.storageAvailable;
        default:
            return false;
    }
};
/**
 * Get all available features as an object
 * Useful for debugging and feature detection
 */
export const getAvailableFeatures = (): Record<string, boolean> => {
    const features = [
        'storage', 'localStorage', 'cookies', 'analytics',
        'serviceWorker', 'webRTC', 'webGL', 'webAudio',
        'geolocation', 'notifications', 'push', 'indexedDB'
    ] as const;
    return features.reduce((acc, feature) => {
        acc[feature] = isFeatureAvailable(feature);
        return acc;
    }, {} as Record<string, boolean>);
};
